// src/services/WebSocketService.js

class WebSocketService {
    constructor() {
        this.socket = null;
        this.listeners = new Map();
        this.eventListeners = new Map(); // For connection events: 'open', 'close', 'error'
        this.connect();
    }

    // --- Event Emitter Logic for Connection Status ---
    on(event, callback) {
        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, new Set());
        }
        this.eventListeners.get(event).add(callback);
    }

    off(event, callback) {
        if (this.eventListeners.has(event)) {
            this.eventListeners.get(event).delete(callback);
        }
    }

    emit(event, data) {
        if (this.eventListeners.has(event)) {
            this.eventListeners.get(event).forEach(callback => callback(data));
        }
    }
    // ------------------------------------------------

    connect() {
        if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
            return;
        }

        // Connect to the Node.js backend WebSocket server
        this.socket = new WebSocket(process.env.REACT_APP_WS_URL || 'ws://localhost:5000');

        this.socket.onopen = () => {
            console.log("WebSocket connection to backend established.");
            this.emit('open');
            // Resubscribe to all topics on reconnection
            this.listeners.forEach((_, topic) => {
                this.sendMessage({ type: 'subscribe', topic });
            });
        };

        this.socket.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                if (message.topic && this.listeners.has(message.topic)) {
                    this.listeners.get(message.topic).forEach(callback => {
                        callback(message);
                    });
                }
            } catch (error) {
                console.error("Failed to parse WebSocket message:", error);
            }
        };

        this.socket.onerror = (error) => {
            console.error("WebSocket error:", error);
            // WebSocket 'error' event doesn't contain the error message for security reasons in browsers,
            // but we can signal that an error occurred.
            this.emit('error', "WebSocket connection error.");
        };

        this.socket.onclose = (event) => {
            console.log("WebSocket connection closed. Attempting to reconnect...");
            this.emit('close');
            setTimeout(() => this.connect(), 5000); // Reconnect after 5 seconds
        };
    }

    sendMessage(message) {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            this.socket.send(JSON.stringify(message));
        } else {
            console.error("WebSocket is not connected.");
        }
    }

    subscribe(topic, callback) {
        if (!this.listeners.has(topic)) {
            this.listeners.set(topic, new Set());
        }
        this.listeners.get(topic).add(callback);

        // Send subscription message to the server
        this.sendMessage({ type: 'subscribe', topic });

        // Return an unsubscribe function
        return () => {
            if (this.listeners.has(topic)) {
                this.listeners.get(topic).delete(callback);
                if (this.listeners.get(topic).size === 0) {
                    this.listeners.delete(topic);
                    // Optional: send an 'unsubscribe' message to the server
                }
            }
        };
    }

    disconnect() {
        if (this.socket) {
            this.socket.close();
        }
    }
}

// Export a singleton instance
const instance = new WebSocketService();
export default instance;

